/**
 * Onboarding navigation mapping (contestant-onboarding-intake spec).
 *
 * A pure, total, I/O-free module that maps a resolved onboarding destination to
 * a concrete existing route in `App.tsx`. This is the single encoding of "which
 * route is the application / resume / status / dashboard surface", mirroring the
 * `resolveActiveCompetitionId` swap-point pattern.
 *
 * The `CONTACT_INTAKE` destination is handled inline by the `OnboardingRouter`
 * and is therefore excluded from the input type (`ResolvedDestination`): every
 * non-intake kind is covered exhaustively here.
 *
 * Mapping table (uses only routes that already exist in `App.tsx`):
 *
 * | Destination kind     | Target route       | Carries competitionId |
 * |----------------------|--------------------|-----------------------|
 * | `APPLICATION`        | `/apply`           | yes                   |
 * | `RESUME_RESPONSE`    | `/apply`           | yes                   |
 * | `APPLICATION_STATUS` | `/portal/profile`  | yes                   |
 * | `DASHBOARD`          | `/portal`          | no                    |
 *
 * Validates: Requirements 1.4, 1.5, 3.1, 3.2, 3.3
 */

import type { ResolvedDestination } from '@/components/application-form/onboarding';

/**
 * The concrete route a resolved onboarding destination maps to, plus the
 * competition the destination resolved against when the kind carries one.
 */
export interface NavTarget {
  /** The existing route path to land on. */
  path: string;
  /** The competition the destination resolved against, when the kind carries one. */
  competitionId?: string;
}

/**
 * Total mapping of every non-intake destination to an existing route
 * (Req 1.4, 1.5, 3.1, 3.2, 3.3).
 *
 * The `switch` is exhaustive over the `ResolvedDestination` union; the
 * `never`-typed default makes any future kind a compile-time error rather than
 * an undefined surface at runtime.
 */
export function mapDestinationToPath(destination: ResolvedDestination): NavTarget {
  switch (destination.kind) {
    case 'APPLICATION':
      // Start a new application for the resolved competition (Req 1.4, 3.3).
      return { path: '/apply', competitionId: destination.competitionId };
    case 'RESUME_RESPONSE':
      // Resume an in-progress draft; the same `/apply` surface resumes it (Req 3.1).
      return { path: '/apply', competitionId: destination.competitionId };
    case 'APPLICATION_STATUS':
      // View the status of a submitted application (Req 3.2).
      return { path: '/portal/profile', competitionId: destination.competitionId };
    case 'DASHBOARD':
      // Portal dashboard; carries no competition (Req 1.5, 3.4).
      return { path: '/portal' };
    default: {
      // Exhaustiveness guard: a new non-intake kind must extend this mapping.
      const _exhaustive: never = destination;
      return _exhaustive;
    }
  }
}
